import { useCallback, useEffect, useState } from "react";

import { Button, FileField } from "../../../ui/index.ts";
import { PHASE2_DOC_SLOTS, type DocumentFilesState, type Phase2DocType } from "../doc-types.ts";
import { validatePhase2DocumentFile } from "../utils/validate-phase2-document-file.ts";

import styles from "./documents-step.module.css";

export type DocumentsStepProps = {
  files: DocumentFilesState;
  onFileChange: (docType: Phase2DocType, file: File | null) => void;
  loading: boolean;
  onBack: () => void;
  onNext: () => void | Promise<void>;
  formId?: string;
  hideFooter?: boolean;
  onResetReady?: (reset: () => void) => void;
};

export function DocumentsStep({
  files,
  onFileChange,
  loading,
  onBack,
  onNext,
  formId,
  hideFooter = false,
  onResetReady,
}: DocumentsStepProps) {
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<Phase2DocType, string>>>({});
  const [inputNonce, setInputNonce] = useState<Partial<Record<Phase2DocType, number>>>({});

  const handleFileChange = useCallback(
    (docType: Phase2DocType, file: File | null) => {
      if (!file) {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[docType];
          return next;
        });
        onFileChange(docType, null);
        return;
      }
      const err = validatePhase2DocumentFile(file);
      if (err) {
        setFieldErrors((prev) => ({ ...prev, [docType]: err }));
        onFileChange(docType, null);
        setInputNonce((prev) => ({ ...prev, [docType]: (prev[docType] ?? 0) + 1 }));
        return;
      }
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[docType];
        return next;
      });
      onFileChange(docType, file);
    },
    [onFileChange],
  );

  useEffect(() => {
    onResetReady?.(() => {
      for (const slot of PHASE2_DOC_SLOTS) {
        onFileChange(slot.docType, null);
      }
      setFieldErrors({});
      setInputNonce({});
    });
  }, [onFileChange, onResetReady]);

  return (
    <form
      id={formId}
      className={styles.root}
      onSubmit={(e) => {
        e.preventDefault();
        void onNext();
      }}
      noValidate
    >
      <p className={`body-sm ${styles.lead}`}>
        Upload clear scans or documents. Images must be 2 MB or smaller and PDFs must be 10 MB or smaller.
      </p>
      <ul className={styles.list}>
        {PHASE2_DOC_SLOTS.map((slot) => (
          <li key={slot.docType} className={styles.item}>
            <FileField
              key={`${slot.docType}-${inputNonce[slot.docType] ?? 0}`}
              id={`doc-${slot.docType}`}
              label={slot.label}
              required={slot.required}
              accept="image/*,.pdf,application/pdf"
              disabled={loading}
              error={fieldErrors[slot.docType]}
              stagedFile={files[slot.docType] ?? null}
              onChange={(file) => handleFileChange(slot.docType, file)}
            />
          </li>
        ))}
      </ul>
      {hideFooter ? null : (
        <div className={styles.actions}>
          <Button type="button" variant="muted-outline" disabled={loading} onClick={onBack}>
            Back
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? "Uploading…" : "Next"}
          </Button>
        </div>
      )}
    </form>
  );
}
